import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link2, CheckCircle, AlertTriangle, Loader2, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const IdentityLinkingPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/superadmin/identity-linking-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Kunne ikke hente status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const runMigration = async () => {
    if (!window.confirm('Vil du kjøre identitetskobling nå? Dette knytter sammen brukere med samme e-post på tvers av klubber. Operasjonen er idempotent og trygg å kjøre flere ganger.')) return;
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/superadmin/run-identity-linking`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Migrasjon feilet');
    } finally {
      setRunning(false);
    }
  };

  const isComplete = status?.complete === true;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500 mb-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <Link2 className="h-8 w-8 text-purple-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-2" data-testid="identity-linking-title">
            Identitetskobling (Multi-Klubb)
          </h3>
          <p className="text-gray-400 mb-4 text-sm">
            Knytter sammen brukerkontoer med samme e-post på tvers av klubber ved å tildele en delt <code className="bg-gray-700 px-1 rounded text-xs">identity_id</code>. 
            Bruker eldste kontoens passord og navn som master. Sikker å kjøre flere ganger.
          </p>

          {/* Status card */}
          <div className="bg-gray-900/60 rounded-lg p-4 mb-4 border border-gray-700">
            {loading && (
              <div className="flex items-center text-gray-400 text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Henter status...
              </div>
            )}
            {!loading && status && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-gray-500 text-xs uppercase">Totalt brukere</div>
                  <div className="text-white font-bold text-lg">{status.total_users}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs uppercase">Med identity_id</div>
                  <div className="text-green-400 font-bold text-lg">{status.with_identity_id}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs uppercase">Venter</div>
                  <div className={`font-bold text-lg ${status.without_identity_id > 0 ? 'text-yellow-400' : 'text-gray-400'}`}>
                    {status.without_identity_id}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs uppercase flex items-center gap-1">
                    <Users className="w-3 h-3" />Multi-klubb
                  </div>
                  <div className="text-purple-300 font-bold text-lg">{status.multi_club_emails}</div>
                </div>
              </div>
            )}
            {!loading && status && (
              <div className="mt-3 pt-3 border-t border-gray-700 flex items-center gap-2">
                {isComplete ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-green-300 text-sm font-medium" data-testid="identity-linking-status-complete">
                      Koblingen er fullført. Alle brukere har fått identity_id.
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    <span className="text-yellow-300 text-sm font-medium" data-testid="identity-linking-status-pending">
                      {status.without_identity_id} bruker(e) venter på å bli koblet. Kjør migrasjonen under.
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={runMigration}
              disabled={running}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 text-sm"
              data-testid="run-identity-linking-btn"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {running ? 'Kjører...' : 'Kjør Identitetskobling'}
            </button>
            <button
              onClick={fetchStatus}
              disabled={loading || running}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="refresh-identity-linking-status-btn"
            >
              Oppdater status
            </button>
          </div>

          {/* Error / result */}
          {error && (
            <div className="mt-4 bg-red-900/30 border border-red-500/40 rounded-lg p-3 text-red-300 text-sm">
              <AlertTriangle className="inline h-4 w-4 mr-1" /> {error}
            </div>
          )}
          {result && (
            <div className="mt-4 bg-green-900/20 border border-green-500/40 rounded-lg p-3 text-sm" data-testid="identity-linking-result">
              <div className="text-green-300 font-medium mb-2">
                <CheckCircle className="inline h-4 w-4 mr-1" /> Migrasjon fullført
              </div>
              <div className="text-gray-300 text-xs space-y-0.5">
                <div>Oppdaterte brukere: <span className="text-white font-medium">{result.updated_count}</span></div>
                <div>Unike e-poster: <span className="text-white font-medium">{result.unique_emails}</span></div>
                <div>Multi-klubb e-poster: <span className="text-purple-300 font-medium">{result.multi_club_emails}</span></div>
              </div>
              {result.log && result.log.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-300">Vis full logg</summary>
                  <div className="mt-2 bg-gray-950 rounded p-2 text-xs text-gray-300 font-mono max-h-48 overflow-y-auto">
                    {result.log.map((l, i) => (<div key={i}>• {l}</div>))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdentityLinkingPanel;
